import { useEffect, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';

interface StyleCard {
    title: string;
    subtitle: string;
}

const CARDS: StyleCard[] = [
    { title: 'Minimalist', subtitle: 'Clean · Pure' },
    { title: 'Contemporary', subtitle: 'Bold · Current' },
    { title: 'Organic', subtitle: 'Natural · Fluid' },
    { title: 'Coastal', subtitle: 'Light · Open' },
    { title: 'Heritage', subtitle: 'Craft · Timeless' },
    { title: 'Sustainable', subtitle: 'Low-Impact' },
];

interface Step4StyleProps {
    value: string | null;
    onSelect: (title: string) => void;
    /**
     * CMS-curated photography keyed by style title. Missing entries fall back
     * to a neutral grey placeholder so the modal still renders cleanly.
     */
    images?: Record<string, string>;
}

export const Step4Style = ({ value, onSelect, images }: Step4StyleProps) => {
    return (
        <div className="flex flex-col gap-[1.375rem]">
            <h2 className="text-[1.3125rem] leading-[1.1] font-medium tracking-[-0.02em] text-[#161616] uppercase">
                What draws you in?
            </h2>
            <ul className="grid grid-cols-2 gap-2">
                {CARDS.map((card) => {
                    const isSelected = value === card.title;
                    const imageUrl = images?.[card.title];
                    return (
                        <li key={card.title}>
                            <button
                                type="button"
                                onClick={() => onSelect(card.title)}
                                className={cn(
                                    'group relative flex h-[11.5rem] w-full flex-col justify-end overflow-hidden rounded-[0.75rem] bg-[#9e9e9e] p-3 text-left',
                                    isSelected && 'ring-2 ring-black',
                                )}
                            >
                                {imageUrl && <StyleCardImage src={imageUrl} />}
                                <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                <span className="relative flex flex-col gap-[0.188rem] text-white">
                                    <span className="text-[0.875rem] leading-[1.1] font-medium tracking-[-0.02em] uppercase">
                                        {card.title}
                                    </span>
                                    <span className="text-[0.75rem] leading-[1.1] font-normal tracking-[-0.02em]">
                                        {card.subtitle}
                                    </span>
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

/**
 * Fade the image in once it has fully decoded AND the GPU has committed the
 * bitmap as a texture, so the user sees a single smooth transition over the
 * grey card background — no half-painted progressive render, no sudden swap,
 * and no first-load "pop" caused by texture upload coinciding with the
 * transition start.
 *
 * Pipeline:
 *  1. `decoding="async"` keeps decode off the main thread.
 *  2. `img.decode()` resolves once the bitmap is fully decoded and ready.
 *  3. Two `requestAnimationFrame`s give the compositor a frame to upload
 *     the texture before the opacity animation kicks off.
 *  4. Only then flip `loaded` → fade plays smoothly every time, hot or cold.
 */
const StyleCardImage = ({ src }: { src: string }) => {
    const ref = useRef<HTMLImageElement | null>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const img = ref.current;
        if (!img) return undefined;

        let cancelled = false;
        let raf1 = 0;
        let raf2 = 0;
        const reveal = () => {
            if (cancelled) return;
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    if (!cancelled) setLoaded(true);
                });
            });
        };

        const decoder =
            typeof img.decode === 'function' ? img.decode() : Promise.resolve();
        decoder.then(reveal).catch(() => {
            // Some browsers reject decode() for certain image types — fall
            // back to the load event so we never leave the image hidden.
            if (img.complete) {
                reveal();
            } else {
                img.addEventListener('load', reveal, { once: true });
            }
        });

        return () => {
            cancelled = true;
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
    }, [src]);

    return (
        <img
            ref={ref}
            src={src}
            alt=""
            aria-hidden
            loading="eager"
            decoding="async"
            className={cn(
                'absolute inset-0 size-full scale-100 object-cover transition-[opacity,transform] duration-300 ease-out group-hover:scale-105',
                loaded ? 'opacity-100' : 'opacity-0',
            )}
        />
    );
};
